// Pure geometry for the measure tool (plain [x,y,z] tuples, any frame).

export type V3 = [number, number, number];

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: V3, b: V3): V3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: V3, s: number): V3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: V3, b: V3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const len = (a: V3): number => Math.sqrt(dot(a, a));

export function distance(a: V3, b: V3): number {
  return len(sub(b, a));
}

/** Angle between two face normals in degrees (0 = parallel, 90 = perpendicular). */
export function angleBetweenNormals(n1: V3, n2: V3): number {
  const l = len(n1) * len(n2);
  if (l < 1e-12) return 0;
  const c = Math.max(-1, Math.min(1, dot(n1, n2) / l));
  return (Math.acos(c) * 180) / Math.PI;
}

/**
 * Circle through three points (circumcircle). Returns null when the points
 * are (nearly) collinear. `normal` is the unit normal of the circle's plane.
 */
export function circleFrom3Points(a: V3, b: V3, c: V3): { center: V3; radius: number; normal: V3 } | null {
  const ab = sub(b, a);
  const ac = sub(c, a);
  const n = cross(ab, ac);
  const n2 = dot(n, n);
  const scaleRef = Math.max(dot(ab, ab), dot(ac, ac));
  if (n2 < 1e-12 * scaleRef * scaleRef || n2 === 0) return null;
  // center = a + (|ac|² (n×ab) + |ab|² (ac×n)) / (2|n|²)
  const t1 = scale(cross(n, ab), dot(ac, ac));
  const t2 = scale(cross(ac, n), dot(ab, ab));
  const center = add(a, scale(add(t1, t2), 1 / (2 * n2)));
  const nl = Math.sqrt(n2);
  return { center, radius: distance(center, a), normal: [n[0] / nl, n[1] / nl, n[2] / nl] };
}
